/* Squish Deli popup (sections/sd-popup.liquid). CONTENT role.
   Opens #sd-popup after data-delay seconds or data-scroll % of the page, whichever is first, at most
   once per data-days days. Never while another dialog/drawer is open. Steps: who -> email -> done.
   The email step posts the Shopify customer form with fetch; if Shopify answers with a bot challenge
   (or fetch fails) it falls back to a normal submit and reopens on the code step after the reload.
   QA: ?sd_popup=1 (force open), ?sd_popup=done (code step). */

const STORE_KEY = 'sd-popup-v1';
const PENDING_KEY = 'sd-popup-pending';
const DAY = 864e5;

const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('sd-popup'));

function readStore() {
  try {
    return JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function writeStore(patch) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify({ ...readStore(), ...patch }));
  } catch {
    /* private mode: the popup just shows again next time */
  }
}

function session(key, value) {
  try {
    if (value === undefined) return window.sessionStorage.getItem(key);
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
  return null;
}

if (dialog && !dialog.dataset.sdReady) {
  dialog.dataset.sdReady = 'true';
  init(dialog);
}

/** @param {HTMLDialogElement} dlg */
function init(dlg) {
  const params = new URL(window.location.href).searchParams;
  const qa = params.get('sd_popup');
  const delay = Math.max(5, Number(dlg.dataset.delay || 35)) * 1000;
  const scrollAt = Math.min(0.95, Math.max(0.1, Number(dlg.dataset.scroll || 50) / 100));
  const days = Number(dlg.dataset.days || 14);
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const form = /** @type {HTMLFormElement | null} */ (dlg.querySelector('#sd-popup-form'));
  const email = /** @type {HTMLInputElement | null} */ (dlg.querySelector('#sd-popup-email'));
  const tags = /** @type {HTMLInputElement | null} */ (dlg.querySelector('[data-sd-popup-tags]'));
  const error = dlg.querySelector('[data-sd-popup-error]');
  const submit = dlg.querySelector('[data-sd-popup-submit]');
  const status = dlg.querySelector('[data-sd-popup-status]');
  let lastFocus = /** @type {HTMLElement | null} */ (null);
  let armed = false;
  let timer = 0;

  const phase = () => (window.SquishDeli && window.SquishDeli.holidayPhase ? window.SquishDeli.holidayPhase().phase : document.documentElement.dataset.sdPhase || 'pre');

  function showStep(name) {
    for (const step of dlg.querySelectorAll('[data-sd-popup-step]')) {
      step.hidden = step.getAttribute('data-sd-popup-step') !== name;
    }
    const current = dlg.querySelector(`[data-sd-popup-step="${name}"]`);
    if (!current) return;
    const target =
      name === 'email'
        ? email
        : name === 'who'
          ? current.querySelector('[data-sd-popup-choose]')
          : current.querySelector('[data-sd-popup-focus]');
    if (target instanceof HTMLElement) requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  function setPath(path) {
    dlg.dataset.path = path;
    if (tags) {
      const list = ['newsletter', 'popup', path === 'gift' ? 'gift' : 'self'];
      if (path === 'gift' && phase() === 'pre') list.push('xmas-reminder');
      tags.value = list.join(',');
    }
  }

  function busy() {
    if (document.querySelector('dialog[open]:not(#sd-popup)')) return true;
    if (document.documentElement.hasAttribute('scroll-lock')) return true;
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement) return true;
    return false;
  }

  function open(step = 'who', { record = true } = {}) {
    if (dlg.open) return;
    lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (record) writeStore({ shown: Date.now() });
    disarm();
    try {
      dlg.showModal();
    } catch {
      dlg.setAttribute('open', '');
    }
    if (window.SquishDeli && window.SquishDeli.applyPhase) window.SquishDeli.applyPhase(dlg);
    showStep(step);
  }

  function close() {
    if (!dlg.open) return;
    dlg.close();
  }

  dlg.addEventListener('close', () => {
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  });

  // Backdrop click (the dialog box itself has no padding, so a target of <dialog> means the backdrop)
  dlg.addEventListener('click', (event) => {
    if (event.target === dlg) close();
  });

  for (const btn of dlg.querySelectorAll('[data-sd-popup-close]')) btn.addEventListener('click', close);

  for (const btn of dlg.querySelectorAll('[data-sd-popup-choose]')) {
    btn.addEventListener('click', () => {
      setPath(btn.getAttribute('data-sd-popup-choose') || 'me');
      showStep('email');
    });
  }

  dlg.querySelector('[data-sd-popup-back]')?.addEventListener('click', () => showStep('who'));

  const copyBtn = dlg.querySelector('[data-sd-popup-copy]');
  copyBtn?.addEventListener('click', async () => {
    const code = dlg.querySelector('[data-sd-popup-code]')?.textContent?.trim() || '';
    const label = copyBtn.querySelector('[data-sd-popup-copy-label]');
    try {
      await navigator.clipboard.writeText(code);
      if (label) label.textContent = 'Copied';
      if (status) status.textContent = `Code ${code} copied.`;
    } catch {
      const range = document.createRange();
      const node = dlg.querySelector('[data-sd-popup-code]');
      if (node) {
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      if (label) label.textContent = 'Selected';
    }
  });

  function showError(message) {
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
    if (email) {
      if (message) {
        email.setAttribute('aria-invalid', 'true');
        email.setAttribute('aria-describedby', 'sd-popup-fine');
      } else {
        email.removeAttribute('aria-invalid');
      }
    }
  }

  function done() {
    writeStore({ done: true, shown: Date.now() });
    session(PENDING_KEY, null);
    showStep('done');
    if (window.SquishDeli && window.SquishDeli.squish) {
      const code = dlg.querySelector('.sd-popup__code');
      if (code) window.SquishDeli.squish(code);
    }
  }

  function nativeSubmit() {
    session(PENDING_KEY, dlg.dataset.path || 'me');
    form?.submit();
  }

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    showError('');
    if (!email || !email.value.trim() || !email.checkValidity()) {
      showError('Please enter a full email address, like you@example.com.');
      email?.focus();
      return;
    }
    if (submit) {
      submit.classList.add('is-loading');
      submit.setAttribute('aria-disabled', 'true');
    }
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      const url = response.url || '';
      if (/\/challenge/.test(url)) {
        nativeSubmit();
        return;
      }
      if (/customer_posted=true/.test(url)) {
        done();
        return;
      }
      const html = await response.text();
      // Shopify re-rendered the page with errors: "Email has already been taken" means they are on the list.
      if (/already been taken|already subscribed/i.test(html)) {
        done();
        return;
      }
      showError("That didn't go through. Check the email and try again.");
    } catch {
      nativeSubmit();
    } finally {
      if (submit) {
        submit.classList.remove('is-loading');
        submit.removeAttribute('aria-disabled');
      }
    }
  });

  /* Triggers --------------------------------------------------------------------------------- */
  function tryOpen() {
    if (dlg.open) return;
    if (busy()) {
      window.clearTimeout(timer);
      timer = window.setTimeout(tryOpen, 8000);
      return;
    }
    open('who');
  }

  function onScroll(event) {
    const t = event.target;
    /** @type {Element | null} */
    let el = null;
    if (t === document || t === window || t === document.documentElement || t === document.body) el = document.scrollingElement;
    else if (t instanceof Element && t.clientHeight > window.innerHeight * 0.6) el = t;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    if (max > 200 && el.scrollTop / max >= scrollAt) tryOpen();
  }

  function arm() {
    if (armed) return;
    armed = true;
    timer = window.setTimeout(tryOpen, delay);
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  }

  function disarm() {
    armed = false;
    window.clearTimeout(timer);
    document.removeEventListener('scroll', onScroll, { capture: true });
  }

  // Theme editor: open while the section is selected, never on its own.
  if (designMode) {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail && event.detail.sectionId === dlg.dataset.sectionId) open('who', { record: false });
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail && event.detail.sectionId === dlg.dataset.sectionId) close();
    });
    return;
  }

  // Back from a native (fallback) submit: show the code.
  const pending = session(PENDING_KEY);
  if (pending && /customer_posted=true/.test(window.location.search)) {
    setPath(pending);
    open('done');
    done();
    return;
  }

  if (qa === 'done') {
    setPath(params.get('sd_path') === 'gift' ? 'gift' : 'me');
    open('done', { record: false });
    return;
  }
  if (qa === '1') {
    open('who', { record: false });
    return;
  }

  const store = readStore();
  if (store.done) return;
  if (store.shown && Date.now() - Number(store.shown) < days * DAY) return;
  arm();
}
